import React from 'react';
import {
    Box, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
    SwipeableDrawer, Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import { NavLink, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { visibleMenu } from '../Menu';
import { withLanguage } from '../common/Texts';
import LanguageSwitcher from './LanguageSwitcher';
import CreditsBadge from './CreditsBadge';
import { topbarTokens as t } from './tokens';
import { buildInitials, buildDisplayName } from '../../utils/userDisplay';

export default function MobileDrawer({
    open, onOpen, onClose, language, loggedIn, credits, onAuthAction,
}) {
    const tr = withLanguage(language);
    const { search } = useLocation();
    const first_name = useSelector((state) => state.user.first_name);
    const last_name = useSelector((state) => state.user.last_name);
    const username = useSelector((state) => state.user.username);
    const isStaff = useSelector((state) => state.user.is_staff);
    const initials = buildInitials(first_name, last_name, username);
    const displayName = buildDisplayName(first_name, last_name, username);

    return (
        <SwipeableDrawer
            anchor="right"
            open={open}
            onOpen={onOpen}
            onClose={onClose}
            disableBackdropTransition
            slotProps={{
                paper: { sx: { width: 280, fontFamily: t.font.sans } },
            }}
        >
            {loggedIn && (
                <>
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 36, height: 36,
                            borderRadius: '50%',
                            backgroundColor: t.color.primarySoft,
                            color: t.color.primary,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 13,
                            fontWeight: 600,
                            flexShrink: 0,
                        }}>
                            {initials}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            {displayName && (
                                <Typography sx={{
                                    fontFamily: t.font.sans, fontSize: 14, fontWeight: 600,
                                    color: t.color.fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                    {displayName}
                                </Typography>
                            )}
                            {username && username !== displayName && (
                                <Typography sx={{
                                    fontFamily: t.font.sans, fontSize: 12, color: t.color.fgSubtle,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                    {username}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {Number.isFinite(credits) && (
                        <Box sx={{ px: 2, pb: 2 }}>
                            <CreditsBadge credits={credits} language={language} onNavigate={onClose}/>
                        </Box>
                    )}
                    <Divider sx={{ borderColor: t.color.divider }}/>
                </>
            )}
            <Box sx={{ p: 2 }}>
                <LanguageSwitcher/>
            </Box>
            <Divider sx={{ borderColor: t.color.divider }}/>
            <List>
                {visibleMenu(isStaff).map((item) => (
                    <ListItem disablePadding key={item.pathname}>
                        <ListItemButton
                            component={NavLink}
                            to={{ pathname: item.pathname, search }}
                            end
                            onClick={onClose}
                            sx={{
                                fontFamily: t.font.sans,
                                '&.active': {
                                    backgroundColor: t.color.primarySoft,
                                    color: t.color.primary,
                                },
                            }}
                        >
                            <ListItemText
                                primary={tr(item.label)}
                                slotProps={{
                                    primary: {
                                        sx: { fontFamily: t.font.sans, fontSize: 14, fontWeight: 500 },
                                    },
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
            <Divider sx={{ borderColor: t.color.divider }}/>
            <List>
                <ListItem disablePadding>
                    <ListItemButton
                        onClick={() => { onClose(); onAuthAction(); }}
                        sx={{ color: loggedIn ? t.color.danger : t.color.fg }}
                    >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                            {loggedIn ? <LogoutIcon fontSize="small"/> : <LoginIcon fontSize="small"/>}
                        </ListItemIcon>
                        <ListItemText
                            primary={loggedIn ? tr('logout') : tr('login')}
                            slotProps={{
                                primary: {
                                    sx: { fontFamily: t.font.sans, fontSize: 14, fontWeight: 500 },
                                },
                            }}
                        />
                    </ListItemButton>
                </ListItem>
            </List>
        </SwipeableDrawer>
    );
}
